import { useEffect, useState, useRef } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Loader2 } from 'lucide-react'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'

interface ExtractionProgressProps {
  jobId: string
  onComplete: (result: Record<string, unknown>) => void
  onCancel: () => void
}

const stepMessages: Record<string, string> = {
  'Starting extraction...': 'Extraktion starten...',
  'Loading patterns and template...': 'Patterns und Template laden...',
  'Extracting timetable structure...': 'Timetable-Struktur extrahieren...',
  'Saving extraction results...': 'Ergebnisse speichern...',
}

const stepMessagePrefixes: Array<[string, string]> = [
  ['Pre-matched', 'Token vorab zugeordnet...'],
]

const MAX_RETRIES = 10
/** Maximum duration to poll before giving up (5 minutes). */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000

export function ExtractionProgress({ jobId, onComplete, onCancel }: ExtractionProgressProps) {
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('Extraktion starten...')
  const [status, setStatus] = useState('running')
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const onCompleteRef = useRef(onComplete)
  const onCancelRef = useRef(onCancel)
  const startTimeRef = useRef(0)

  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])
  useEffect(() => { onCancelRef.current = onCancel }, [onCancel])

  useEffect(() => {
    if (!jobId) return

    let pollInterval: ReturnType<typeof setInterval>
    let isCancelled = false

    const poll = async () => {
      try {
        const result = await timetableImportV2Api.getJobStatus(jobId)
        startTimeRef.current = Date.now()
        setRetryCount(0)
        setProgress(result.progress || 0)
        const translated = stepMessages[result.message || '']
        let displayMessage = translated || result.message || 'In Bearbeitung...'
        if (!translated) {
          for (const [prefix, translation] of stepMessagePrefixes) {
            if (result.message?.startsWith(prefix)) {
              displayMessage = translation
              break
            }
          }
        }
        setMessage(displayMessage)
        setStatus(result.status)

        if (result.status === 'completed') {
          // Accept both session_id (single) and batch_id (multi-timetable)
          const hasSessionId = result.result?.session_id
          const hasBatchId = result.result?.batch_id || result.result?.is_multi_timetable
          if (!hasSessionId && !hasBatchId) {
            setError('Extraktion fehlgeschlagen: Keine Daten erhalten')
            setStatus('failed')
          } else {
            try {
              onCompleteRef.current(result.result || {})
            } catch (err) {
              console.error('onComplete failed:', err)
              setError('Fehler beim Verarbeiten der Ergebnisse')
              setStatus('failed')
            }
          }
        } else if (result.status === 'failed') {
          setError(result.error || 'Extraktion fehlgeschlagen')
          setStatus('failed')
        }

        if (result.status === 'completed' || result.status === 'failed' || result.status === 'cancelled') {
          clearInterval(pollInterval)
        }
      } catch {
        if (isCancelled) return
        if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
          setError('Verbindung zum Server verloren. Bitte versuche es erneut.')
          setStatus('failed')
          clearInterval(pollInterval)
          return
        }
        setRetryCount(prev => {
          const next = prev + 1
          if (next >= MAX_RETRIES) {
            setError('Verbindung zum Server verloren. Bitte versuche es erneut.')
            setStatus('failed')
            return next
          }
          return next
        })
      }
    }

    startTimeRef.current = Date.now()
    poll()
    pollInterval = setInterval(poll, 2000)
    return () => {
      isCancelled = true
      clearInterval(pollInterval)
    }
  }, [jobId])

  const handleCancel = async () => {
    try {
      await timetableImportV2Api.cancelJob(jobId)
    } catch {
      // ignore cancel errors
    }
    onCancelRef.current()
  }

  if (status === 'failed') {
    return (
      <Modal isOpen={true} onClose={onCancel} title="Extraktion fehlgeschlagen" size="sm">
        <div className="space-y-4">
          <div className="text-red-600 font-medium">{error}</div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onCancel}>Schliessen</Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={true} onClose={() => {}} title="Extraktion läuft" size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="font-medium">{message}</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>

        <div className="text-sm text-gray-500">{progress}% abgeschlossen</div>
        {retryCount > 0 && (
          <div className="text-xs text-orange-600">
            Verbindung wiederherstellen... ({retryCount}/{MAX_RETRIES})
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            Abbrechen
          </Button>
        </div>
      </div>
    </Modal>
  )
}
